import React, { useMemo, useState } from 'react';

import { compareBoardColumns } from '@aline/common';
import { Typography } from '@mui/material';
import { PICKER_CHIP_ICON_SIZE, usePickerChipStyles } from 'src/components/generic';
import { LucideIconRenderer } from 'src/components/generic/LucideIconRenderer';
import { BoardList } from 'src/components/workflow/board/board-toolbar/BoardFilterPicker';
import { ProgressiveColumnIcon } from 'src/components/workflow/board/settings/ColumnItem';
import { getColumnPositionInCategory } from 'src/components/workflow/phase-column-helpers';
import {
  SigningLeaveDialog,
  UnparkConfirmDialog,
  useWorkflowColumnChangeGuard,
} from 'src/components/workflow/use-workflow-column-change-guard';
import {
  PriorityDisplay,
  WorkflowPriority,
} from 'src/components/workflow/workflow-list/cells/WorkflowPriority';
import {
  ColumnPickerPopover,
  WorkflowBoardRelocationDialog,
} from 'src/components/workflow/WorkflowBoardRelocationDialog';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import type { WorkflowDetailMeta } from './WorkflowDetailContext';

const useStyles = makeStyles()(theme => ({
  root: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

interface BoardMetadataProps {
  workflow: WorkflowDetailMeta;
}

export function BoardMetadata({ workflow }: BoardMetadataProps) {
  const { classes } = useStyles();
  const { getPickerChipClassName, pickerChipIconClassName, pickerChipLabelClassName } =
    usePickerChipStyles();
  const { data: boardsData } = Apollo.useGetBoardsQuery();
  const [updateWorkflow] = Apollo.useUpdateWorkflowMutation();
  const [relocationDialog, setRelocationDialog] = useState<{
    open: boolean;
    targetBoardId: string | null;
  }>({ open: false, targetBoardId: null });

  const currentBoard = boardsData?.boards?.find(b =>
    b.columns?.some(c => c.id === workflow.column.id)
  );

  const sortedColumns = useMemo(
    () => [...(currentBoard?.columns ?? [])].sort(compareBoardColumns),
    [currentBoard?.columns]
  );

  const columnPosition = useMemo(() => {
    if (!sortedColumns.length) return { index: 1, total: 1 };
    return getColumnPositionInCategory(workflow.column, sortedColumns);
  }, [workflow.column, sortedColumns]);

  const {
    confirmUnparkMove,
    dismissSigningLeave,
    dismissUnpark,
    handleColumnChange,
    pendingUnparkColumn,
    signingLeaveOpen,
  } = useWorkflowColumnChangeGuard({
    columns: sortedColumns,
    currentColumn: workflow.column,
    onConfirmColumnChange: async (columnId, { confirmUnpark }) => {
      await updateWorkflow({
        variables: {
          input: { confirmUnpark, props: { columnId }, workflowId: workflow.id },
        },
      });
    },
    workflowState: workflow.state,
  });

  return (
    <div className={classes.root}>
      <BoardList
        boards={boardsData?.boards ?? []}
        renderTrigger={(board, onClick, isOpen) => (
          <div className={getPickerChipClassName(isOpen)} onClick={onClick}>
            {board && (
              <div className={pickerChipIconClassName}>
                <LucideIconRenderer
                  color={board.color}
                  icon={board.icon}
                  size={PICKER_CHIP_ICON_SIZE}
                />
              </div>
            )}
            <Typography noWrap className={pickerChipLabelClassName} variant="subtitle2">
              {board?.name ?? 'No Board'}
            </Typography>
          </div>
        )}
        selectedBoardId={currentBoard?.id}
        onBoardChange={targetBoardId => setRelocationDialog({ open: true, targetBoardId })}
      />

      {workflow.variant !== Apollo.WorkflowVariant.Template && (
        <ColumnPickerPopover
          columns={sortedColumns}
          renderTrigger={(_, isOpen) => (
            <div className={getPickerChipClassName(isOpen)}>
              <ProgressiveColumnIcon
                boxSize={PICKER_CHIP_ICON_SIZE}
                category={workflow.column.category}
                className={pickerChipIconClassName}
                color={workflow.column.color}
                iconSize={PICKER_CHIP_ICON_SIZE}
                index={columnPosition.index}
                showBackground={false}
                showBorder={false}
                total={columnPosition.total}
              />
              <Typography noWrap className={pickerChipLabelClassName} variant="body2">
                {workflow.column.name}
              </Typography>
            </div>
          )}
          selectedColumnId={workflow.column.id}
          onColumnChange={handleColumnChange}
        />
      )}

      {workflow.variant !== Apollo.WorkflowVariant.Template && (
        <WorkflowPriority
          priority={workflow.priority}
          renderTrigger={priority => <PriorityDisplay priority={priority} />}
          onPriorityChange={async priority => {
            await updateWorkflow({
              variables: {
                input: { props: { priority }, workflowId: workflow.id },
              },
            });
          }}
        />
      )}
      <SigningLeaveDialog open={signingLeaveOpen} onClose={dismissSigningLeave} />
      <UnparkConfirmDialog
        currentColumnName={workflow.column.name}
        open={Boolean(pendingUnparkColumn)}
        targetColumnName={pendingUnparkColumn?.name}
        onClose={dismissUnpark}
        onConfirm={confirmUnparkMove}
      />
      {relocationDialog.open && relocationDialog.targetBoardId && (
        <WorkflowBoardRelocationDialog
          currentBoardId={currentBoard?.id ?? null}
          currentColumnId={workflow.column.id}
          currentDoneColumnId={workflow.doneColumn?.id}
          open={relocationDialog.open}
          phases={workflow.phases ?? []}
          startedAt={workflow.startedAt}
          targetBoardId={relocationDialog.targetBoardId}
          workflowId={workflow.id}
          workflowName={workflow.name}
          onClose={() => setRelocationDialog({ open: false, targetBoardId: null })}
        />
      )}
    </div>
  );
}
